import { useCallback, useEffect, useMemo } from 'react';
import {
  VideoSettings,
  SUPPORTED_VIDEO_MODELS,
  VideoModel,
  VIDEO_TO_VIDEO_MODELS,
  VIDEO_MODEL_CONFIG,
  type VideoModelConfig,
  type VideoOptions,
  type VideoFrame,
} from '@/features/chats/ui/ChatOptions/VideoGeneration/VideoGenerationOptions.types';
import { ChatOptionsProps } from '../ChatOptions.types';
import { VeoGenerationOptions } from './models/VeoOptions';
import { RunwayGenerationOptions } from './models/RunwayOptions';
import { OmniGenerationOptions } from './models/OmniOptions';
import { useOptionsManager } from '../hooks/useOptionsManager';
import { VideoFrameUploadButton } from '../../VideoFrameUploadButton';
import { useFilteredModels } from '../hooks/useFilteredModels';
import { useCurrentAgent, useIsAiBusy } from '@/features/chats/stores';
import {
  getOptionsForModel,
  hasVeoFramesWithReferenceImages,
  hasVeoReferenceImages,
  imagesBlockVeoFrames,
  type VeoFrameSettings,
} from './videoOptionsHelpers';
import { useTranslation } from 'react-i18next';

/**
 * Spreads `options` first so sibling keys owned by other components
 * (e.g. `llmOptions` written by the AI Model Selector in ChatInputV2)
 * are preserved across popup updates.
 */
function toStoreVideoOptions(options: VideoOptions): VideoOptions {
  return {
    ...options,
    settings: {
      ...options.settings,
    },
  };
}

export const VideoGenerationOptions = ({
  onOptionUpdated,
  attachments,
  setIsLoading,
  onFilePickerOpenChange,
  options: parentOptions,
}: ChatOptionsProps) => {
  const { t } = useTranslation('chats');

  const currentAgent = useCurrentAgent();
  const isDisabled = useIsAiBusy();
  const hasVideos = attachments.some((attachment) => attachment.mimeType.startsWith('video/'));
  const hasReferenceImages = hasVeoReferenceImages(attachments);
  const filteredModels = useFilteredModels(
    hasVideos && VIDEO_TO_VIDEO_MODELS.find((m) => currentAgent?.enabledModels?.includes(m.value))
      ? VIDEO_TO_VIDEO_MODELS
      : SUPPORTED_VIDEO_MODELS,
  );
  const hasModels = filteredModels.length > 0;

  const defaultOptions = useMemo(() => {
    const firstModel = filteredModels[0]?.value ?? SUPPORTED_VIDEO_MODELS[0].value;
    return getOptionsForModel(firstModel, undefined, { hasReferenceImages });
  }, [filteredModels, hasReferenceImages]);

  const { options, setOptions } = useOptionsManager({
    defaultOptions,
    onOptionUpdated,
    toStore: toStoreVideoOptions,
  });

  const dropdownModels = useMemo(
    () => filteredModels.map((m) => ({ ...m, secondaryLabel: m.provider })),
    [filteredModels],
  );

  // `VIDEO_MODEL_CONFIG` is typed as a total Record over VideoModel, but
  // `options.settings.model` comes from the store and can briefly hold a value
  // outside that union (a stale/out-of-list selection during a model switch).
  // Look it up as a plain string map so a miss is `undefined` rather than a
  // type-system lie, and fall back to a frame-less config; the validation effect
  // below corrects the model on the next render.
  const modelConfigMap: Record<string, VideoModelConfig | undefined> = VIDEO_MODEL_CONFIG;
  const modelConfig = modelConfigMap[options.settings.model];
  const showStartFrame = modelConfig?.enableStartFrame ?? false;
  const showEndFrame = modelConfig?.enableEndFrame ?? false;
  const framesBlockedByImages = imagesBlockVeoFrames(options.settings.model, hasReferenceImages);
  // A frame already set stays enabled so it can still be cleared.
  const frameLocked = (frame: VideoFrame | null | undefined) =>
    isDisabled || !hasModels || (framesBlockedByImages && !frame);

  // Validate and fall back the model if not in the allowed list.
  useEffect(() => {
    const isAllowed = dropdownModels.some((m) => m.value === options.settings.model);
    if (!isAllowed && dropdownModels.length > 0) {
      const fallback = dropdownModels[0].value;
      setOptions(getOptionsForModel(fallback, undefined, { hasReferenceImages }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dropdownModels]);

  // Sync model from the right-side AI Model Selector (lives in ChatInputV2 and
  // writes to options.llmOptions.model). Without this, the popup would show
  // fields for its own default model instead of the one the user picked in the
  // chip. Reset of dependent fields is delegated to getOptionsForModel.
  const externalSelectedModel = (parentOptions?.llmOptions as { model?: string } | undefined)
    ?.model;
  useEffect(() => {
    if (!externalSelectedModel) return;
    if (externalSelectedModel === options.settings.model) return;
    const isAllowed = dropdownModels.some((m) => m.value === externalSelectedModel);
    if (!isAllowed) return;
    setOptions(
      getOptionsForModel(externalSelectedModel as VideoModel, options.settings, {
        hasReferenceImages,
      }),
    );
  }, [externalSelectedModel, options.settings, dropdownModels, setOptions, hasReferenceImages]);

  const handleOptionChange = useCallback(
    (newSettings: VideoSettings) => {
      setOptions({ settings: newSettings });
    },
    [setOptions],
  );

  const handleStartFrameChange = useCallback(
    (frame: VideoFrame | null) => {
      setOptions((prev) => ({
        ...prev,
        settings: { ...prev.settings, startFrame: frame },
      }));
    },
    [setOptions],
  );

  const handleEndFrameChange = useCallback(
    (frame: VideoFrame | null) => {
      setOptions((prev) => ({
        ...prev,
        settings: { ...prev.settings, endFrame: frame },
      }));
    },
    [setOptions],
  );

  const optionComponent = useMemo(() => {
    if (options.settings.model === 'veo-3.1' || options.settings.model === 'veo-3.1-fast') {
      return (
        <VeoGenerationOptions
          value={options.settings}
          onChange={handleOptionChange}
          disabled={!hasModels}
          hasReferenceImages={hasReferenceImages}
        />
      );
    }
    if (options.settings.model === 'gemini-omni-1.1-flash') {
      return (
        <OmniGenerationOptions
          value={options.settings}
          onChange={handleOptionChange}
          disabled={!hasModels}
        />
      );
    }
    return (
      <RunwayGenerationOptions
        value={options.settings}
        onChange={handleOptionChange}
        hasVideos={hasVideos}
        disabled={!hasModels}
      />
    );
  }, [options, handleOptionChange, hasVideos, hasModels, hasReferenceImages]);

  const framesDisabledReason = (() => {
    if (!hasModels) return t('chats.videoGenerationOptions.noVideoModelsAreEnabled');
    if (isDisabled) return t('chats.videoGenerationOptions.availableOnceTheCurrent');
    // A frame already set stays clearable here, so the way out is either side, as the composer says.
    if (hasVeoFramesWithReferenceImages(options.settings as VeoFrameSettings, hasReferenceImages)) {
      return t('chats.chatInputV2.veoFramesWithImages');
    }
    if (framesBlockedByImages) return t('chats.videoGenerationOptions.veoFramesBlockedByImages');
    return null;
  })();

  return (
    <div className="flex flex-col gap-4 w-80">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-foreground">
          {t('chats.videoGenerationOptions.videoAgentSettings')}
        </span>
      </div>

      {optionComponent}

      {showStartFrame && (
        <div className="flex flex-col gap-2">
          <span className="text-xs font-medium text-foreground-subtle">
            {t('chats.videoGenerationOptions.startFrame')}
          </span>
          <VideoFrameUploadButton
            label={t('chats.videoGenerationOptions.startFrame')}
            frame={options.settings.startFrame ?? null}
            onChange={handleStartFrameChange}
            onLoading={setIsLoading}
            onFilePickerOpenChange={onFilePickerOpenChange}
            disabled={frameLocked(options.settings.startFrame)}
          />
        </div>
      )}
      {showEndFrame && (
        <div className="flex flex-col gap-2">
          <span className="text-xs font-medium text-foreground-subtle">
            {t('chats.videoGenerationOptions.endFrame')}
          </span>
          <VideoFrameUploadButton
            label={t('chats.videoGenerationOptions.endFrame')}
            frame={options.settings.endFrame ?? null}
            onChange={handleEndFrameChange}
            onLoading={setIsLoading}
            onFilePickerOpenChange={onFilePickerOpenChange}
            disabled={frameLocked(options.settings.endFrame)}
          />
        </div>
      )}
      {(showStartFrame || showEndFrame) && framesDisabledReason && (
        <span className="text-xs text-muted-foreground">{framesDisabledReason}</span>
      )}
    </div>
  );
};
